import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB limit
const ALLOWED_EXTENSIONS = ["png", "jpg", "jpeg"];
// Strict regex supporting ONLY SKU;Front and SKU;Back (with or without curly braces)
const FILENAME_REGEX = /^\{?([^;}]+);(Front|Back)\}?\.(png|jpg|jpeg)$/i;

export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const validateFile = (file) => {
  const name = file.name || "";
  const size = file.size || 0;

  // 1. File size check
  if (size > MAX_FILE_SIZE) {
    const sizeMb = (size / (1024 * 1024)).toFixed(1);
    return {
      isValid: false,
      error: `File size exceeds 10MB limit (${sizeMb} MB)`,
      sku: null,
      side: null,
    };
  }

  // 2. Extension check
  const extMatch = name.match(/\.([a-z0-9]+)$/i);
  const ext = extMatch ? extMatch[1].toLowerCase() : "";
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return {
      isValid: false,
      error: `Invalid file format (.${ext || "unknown"}). Only PNG, JPG, JPEG allowed`,
      sku: null,
      side: null,
    };
  }

  // 3. Filename format check (Strictly SKU;Front or SKU;Back)
  const match = name.match(FILENAME_REGEX);
  if (!match) {
    return {
      isValid: false,
      error: "Filename must be format SKU;Front or SKU;Back (e.g., Sharara-001;Front.jpg)",
      sku: null,
      side: null,
    };
  }

  const sku = match[1].trim();
  if (!sku) {
    return {
      isValid: false,
      error: "SKU identifier cannot be blank (e.g., SKU-101;Front.jpg)",
      sku: null,
      side: null,
    };
  }

  const rawSide = match[2].toLowerCase();
  const side = rawSide === "front" ? "Front" : "Back";

  return {
    isValid: true,
    error: null,
    sku,
    side,
  };
};

export const useBulkProductImagesUploadModal = ({ isOpen, onClose, onRefreshProducts }) => {
  const token = useSelector((state) => state.auth?.token);
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [activeTab, setActiveTab] = useState("valid"); // 'valid' | 'invalid'
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const resetState = () => {
    setFiles([]);
    setActiveTab("valid");
    setIsDragOver(false);
    setIsUploading(false);
    setUploadResult(null);
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const validFiles = files.filter((f) => f.isValid);
  const invalidFiles = files.filter((f) => !f.isValid);

  const handleFilesAdded = (incomingFiles) => {
    if (!incomingFiles || incomingFiles.length === 0) return;

    const fileList = Array.from(incomingFiles);
    const newItems = fileList.map((file) => {
      const validation = validateFile(file);
      let previewUrl = "";
      try {
        previewUrl = URL.createObjectURL(file);
      } catch (err) {
        previewUrl = "";
      }

      return {
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        name: file.name,
        size: file.size,
        previewUrl,
        isValid: validation.isValid,
        error: validation.error,
        sku: validation.sku,
        side: validation.side,
      };
    });

    if (uploadResult) {
      files.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
      setUploadResult(null);
      setFiles(newItems);
      const hasValid = newItems.some((f) => f.isValid);
      const hasInvalid = newItems.some((f) => !f.isValid);

      if (hasValid && !hasInvalid) setActiveTab("valid");
      else if (!hasValid && hasInvalid) setActiveTab("invalid");
      return;
    }

    setFiles((prev) => {
      const updated = [...prev, ...newItems];
      const hasValid = updated.some((f) => f.isValid);
      const hasInvalid = updated.some((f) => !f.isValid);

      if (hasValid && !hasInvalid) setActiveTab("valid");
      else if (!hasValid && hasInvalid) setActiveTab("invalid");

      return updated;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer?.files) {
      handleFilesAdded(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileSelect = (e) => {
    if (e.target.files) {
      handleFilesAdded(e.target.files);
    }
    e.target.value = "";
  };

  const handleRemoveFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleClearAll = () => {
    files.forEach((f) => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    setFiles([]);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (validFiles.length === 0) {
      toast.error("No valid image files to upload!");
      return;
    }

    setIsUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      validFiles.forEach((item) => {
        formData.append("images", item.file);
      });

      const response = await apiHandler({
        url: endpoint.BULK_UPLOAD_PRODUCT_IMAGES,
        method: "POST",
        data: formData,
        authToken: { token },
        headers: {
          "Content-Type": undefined,
        },
      });

      const res = response?.data;
      const resData = res?.data || res || {};
      const failedProducts = resData?.failedProducts || [];
      const failedCount =
        resData?.failedCount ?? failedProducts.length;
      const succeededCount =
        resData?.succeededCount ?? resData?.successfulProducts?.length ?? 0;
      const totalSkus =
        resData?.totalSkus ?? (succeededCount + failedCount);
      const resMessage =
        res?.message || "Bulk image mapping completed.";
      const statusType =
        res?.status || (failedCount > 0 ? "error" : "success");

      // Handle failure or partial failure response from API
      if (
        failedCount > 0 ||
        failedProducts.length > 0 ||
        res?.status === "error" ||
        res?.success === false
      ) {
        setUploadResult({
          status: statusType,
          message: resMessage,
          data: {
            totalSkus,
            succeededCount,
            failedCount,
            successfulProducts: resData.successfulProducts || [],
            failedProducts,
            unmatchedFiles: resData.unmatchedFiles || [],
          },
        });

        toast.error(
          resMessage ||
            `Bulk image mapping completed with ${failedCount} error(s).`
        );
        if (succeededCount > 0 && onRefreshProducts) {
          onRefreshProducts();
        }
      } else if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 207 ||
        res?.status === "success" ||
        res?.success === true
      ) {
        setUploadResult({
          status: "success",
          message: resMessage,
          data: {
            totalSkus,
            succeededCount,
            failedCount: 0,
            successfulProducts: resData.successfulProducts || [],
            failedProducts: [],
            unmatchedFiles: resData.unmatchedFiles || [],
          },
        });
        toast.success(resMessage);
        if (onRefreshProducts) {
          onRefreshProducts();
        }
      } else {
        const errorMsg =
          res?.message ||
          res?.error ||
          "Failed to upload bulk product images.";
        setUploadResult({
          status: "error",
          message: errorMsg,
          data: {
            totalSkus: validFiles.length,
            succeededCount: 0,
            failedCount: validFiles.length,
            successfulProducts: [],
            failedProducts: [
              {
                sku: "-",
                error: errorMsg,
              },
            ],
            unmatchedFiles: [],
          },
        });
        toast.error(errorMsg);
      }
    } catch (err) {
      const errRes = err.response?.data;
      const errData = errRes?.data || errRes || {};
      const failedProducts = errData?.failedProducts || [];
      const failedCount =
        errData?.failedCount ??
        (failedProducts.length > 0 ? failedProducts.length : validFiles.length);
      const succeededCount = errData?.succeededCount ?? 0;

      if (
        errRes &&
        (failedProducts.length > 0 || errRes.message || errData.failedCount > 0)
      ) {
        setUploadResult({
          status: "error",
          message: errRes.message || "Bulk image mapping failed.",
          data: {
            totalSkus: errData.totalSkus ?? (succeededCount + failedCount),
            succeededCount,
            failedCount,
            successfulProducts: errData.successfulProducts || [],
            failedProducts,
            unmatchedFiles: errData.unmatchedFiles || [],
          },
        });
        toast.error(
          errRes.message || "Bulk image mapping completed with errors."
        );
        if (succeededCount > 0 && onRefreshProducts) {
          onRefreshProducts();
        }
      } else {
        const errorMsg =
          err.response?.data?.message ||
          err.message ||
          "An error occurred during bulk image upload.";
        setUploadResult({
          status: "error",
          message: errorMsg,
          data: {
            totalSkus: validFiles.length,
            succeededCount: 0,
            failedCount: validFiles.length,
            successfulProducts: [],
            failedProducts: [
              {
                sku: "-",
                error: errorMsg,
              },
            ],
            unmatchedFiles: [],
          },
        });
        toast.error(errorMsg);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseModal = () => {
    resetState();
    onClose();
    if (onRefreshProducts) {
      onRefreshProducts();
    }
  };

  const clearUploadResult = () => {
    setUploadResult(null);
    setFiles([]);
    setActiveTab("valid");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return {
    fileInputRef,
    files,
    activeTab,
    setActiveTab,
    isDragOver,
    isUploading,
    uploadResult,
    validFiles,
    invalidFiles,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleFileSelect,
    handleRemoveFile,
    handleClearAll,
    handleUpload,
    handleCloseModal,
    clearUploadResult,
  };
};
